import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  ApiError,
  sendChatMessage,
  type ChatAttachment,
  type ChatMessage,
  type ChatSearchedItem,
} from '../api';

interface DisplayMessage extends ChatMessage {
  // searches is attached to assistant turns only — surfaces what
  // Claude actually web-searched for, so the user can audit the
  // sources behind a recommendation.
  searches?: ChatSearchedItem[];
}

const SUGGESTED_PROMPTS = [
  'Which of my investments are performing worst, and why?',
  'How balanced is my portfolio across asset classes vs my targets?',
  'What recent news might affect my biggest holding?',
  'Should I rebalance? Anything urgent?',
];

// Mirror the backend whitelist (pkg/aichat/aichat.go). Single source of
// truth lives server-side; this is the UI affordance.
const ALLOWED_FORMATS: Record<string, string> = {
  pdf: 'pdf',
  csv: 'csv',
  doc: 'doc',
  docx: 'docx',
  xls: 'xls',
  xlsx: 'xlsx',
  html: 'html',
  htm: 'html',
  txt: 'txt',
  md: 'md',
  markdown: 'md',
};
const FILE_INPUT_ACCEPT = '.pdf,.csv,.doc,.docx,.xls,.xlsx,.html,.htm,.txt,.md,.markdown';
const MAX_ATTACHMENT_BYTES = 3 * 1024 * 1024; // matches aichat.MaxAttachmentBytes

export function ChatPanel({ accountId }: { accountId: string }) {
  const [messages, setMessages] = useState<DisplayMessage[]>([]);
  const [input, setInput] = useState('');
  const [pending, setPending] = useState<ChatAttachment[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Pin the conversation to the bottom on new messages — same
  // affordance every chat UI uses.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  async function send(prompt: string) {
    const trimmed = prompt.trim();
    if (busy) return;
    // Allow empty text only if at least one attachment is queued —
    // backend enforces the same. "Look at this doc" with no
    // accompanying question is a valid prompt.
    if (!trimmed && pending.length === 0) return;
    setError(null);
    const userMsg: DisplayMessage = {
      role: 'user',
      content: trimmed,
      attachments: pending.length > 0 ? pending : undefined,
    };
    const next: DisplayMessage[] = [...messages, userMsg];
    setMessages(next);
    setInput('');
    setPending([]);
    setBusy(true);
    try {
      // Server expects messages with the current user turn as the last
      // entry. Strip the local-only `searches` field before sending,
      // but preserve attachments.
      const wireMessages: ChatMessage[] = next.map((m) => ({
        role: m.role,
        content: m.content,
        attachments: m.attachments,
      }));
      const resp = await sendChatMessage(accountId, wireMessages);
      setMessages((curr) => [
        ...curr,
        { role: 'assistant', content: resp.reply, searches: resp.searches_used },
      ]);
    } catch (err) {
      setError(asMessage(err));
      // Roll the user turn back so they can edit and retry without
      // a phantom unanswered question hanging in the history. Restore
      // the pending attachments too — re-uploading would be annoying.
      setMessages((curr) => curr.slice(0, -1));
      setInput(trimmed);
      if (userMsg.attachments) setPending(userMsg.attachments);
    } finally {
      setBusy(false);
    }
  }

  async function onFilesSelected(files: FileList) {
    setError(null);
    const next: ChatAttachment[] = [...pending];
    for (const file of Array.from(files)) {
      const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
      const format = ALLOWED_FORMATS[ext];
      if (!format) {
        setError(`Unsupported file type: ${file.name}. Allowed: ${FILE_INPUT_ACCEPT}.`);
        continue;
      }
      if (file.size > MAX_ATTACHMENT_BYTES) {
        setError(
          `${file.name} is ${(file.size / 1024 / 1024).toFixed(1)} MB, over the 3 MB limit.`
        );
        continue;
      }
      try {
        const data = await fileToBase64(file);
        next.push({ name: file.name, format, data });
      } catch (err) {
        setError(`Failed to read ${file.name}: ${asMessage(err)}`);
      }
    }
    setPending(next);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function removeAttachment(index: number) {
    setPending((curr) => curr.filter((_, i) => i !== index));
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    void send(input);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      <div
        ref={scrollRef}
        style={{
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: 10,
          padding: '0.85rem 1rem',
          minHeight: 220,
          maxHeight: 480,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.6rem',
        }}
      >
        {messages.length === 0 && !busy && (
          <div style={{ color: '#64748b', fontSize: '0.85rem' }}>
            <p style={{ margin: '0 0 0.5rem' }}>
              Ask anything about your portfolio. Claude can see your vehicles, holdings, properties,
              and allocation targets, and will search the web for current news when relevant. Use 📎
              to attach a document (PDF, DOCX, XLSX, MD, CSV…) as guidance — useful for sharing an
              IFA's recommendation, a pension scheme doc, or a spreadsheet.
            </p>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                gap: '0.4rem',
                marginTop: '0.4rem',
              }}
            >
              {SUGGESTED_PROMPTS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => void send(p)}
                  style={{
                    textAlign: 'left',
                    padding: '0.5rem 0.7rem',
                    background: '#fff',
                    border: '1px solid #cbd5e1',
                    borderRadius: 8,
                    color: '#334155',
                    fontSize: '0.82rem',
                    cursor: 'pointer',
                  }}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <MessageBubble key={i} msg={m} />
        ))}

        {busy && (
          <div
            style={{
              alignSelf: 'flex-start',
              color: '#64748b',
              fontSize: '0.85rem',
              fontStyle: 'italic',
            }}
          >
            Claude is thinking…
          </div>
        )}
      </div>

      {error && <p style={{ color: '#b91c1c', margin: 0, fontSize: '0.85rem' }}>{error}</p>}

      {pending.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
          {pending.map((a, i) => (
            <AttachmentChip
              key={i}
              name={a.name}
              bytes={dataLen(a.data)}
              onRemove={() => removeAttachment(i)}
            />
          ))}
        </div>
      )}

      <form onSubmit={onSubmit} style={{ display: 'flex', gap: '0.5rem' }}>
        <input
          ref={fileInputRef}
          type="file"
          accept={FILE_INPUT_ACCEPT}
          multiple
          style={{ display: 'none' }}
          onChange={(e) => {
            if (e.target.files && e.target.files.length > 0) {
              void onFilesSelected(e.target.files);
            }
          }}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={busy}
          title="Attach a doc — Claude will use it as guidance"
        >
          📎
        </button>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={
            pending.length > 0 ? 'Add an instruction (optional)…' : 'Ask about your portfolio…'
          }
          disabled={busy}
          style={{ flex: 1, padding: '0.5rem 0.75rem' }}
        />
        <button type="submit" disabled={busy || (!input.trim() && pending.length === 0)}>
          {busy ? 'Sending…' : 'Send'}
        </button>
      </form>
    </div>
  );
}

function AttachmentChip({
  name,
  bytes,
  onRemove,
}: {
  name: string;
  bytes: number;
  onRemove?: () => void;
}) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.4rem',
        background: '#eef2ff',
        border: '1px solid #c7d2fe',
        borderRadius: 999,
        padding: '0.2rem 0.6rem',
        fontSize: '0.78rem',
        color: '#334155',
      }}
    >
      📄 {name} <span style={{ color: '#64748b' }}>· {formatBytes(bytes)}</span>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#64748b',
            cursor: 'pointer',
            padding: 0,
            fontSize: '0.85rem',
          }}
          title="Remove"
        >
          ✕
        </button>
      )}
    </span>
  );
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // FileReader gives us "data:<mime>;base64,<payload>" — strip the prefix.
      const idx = result.indexOf(',');
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsDataURL(file);
  });
}

// dataLen approximates raw byte count from a base64 string. Standard
// base64: 4 chars per 3 bytes, minus padding.
function dataLen(b64: string): number {
  const pad = b64.match(/=+$/)?.[0]?.length ?? 0;
  return Math.floor((b64.length * 3) / 4) - pad;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function MessageBubble({ msg }: { msg: DisplayMessage }) {
  const isUser = msg.role === 'user';
  return (
    <div
      style={{
        alignSelf: isUser ? 'flex-end' : 'flex-start',
        maxWidth: '85%',
        background: isUser ? '#eef2ff' : '#fff',
        border: `1px solid ${isUser ? '#c7d2fe' : '#e2e8f0'}`,
        borderRadius: 10,
        padding: '0.6rem 0.85rem',
        fontSize: '0.9rem',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {msg.attachments && msg.attachments.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.3rem',
            marginBottom: msg.content ? '0.4rem' : 0,
          }}
        >
          {msg.attachments.map((a, i) => (
            <AttachmentChip key={i} name={a.name} bytes={dataLen(a.data)} />
          ))}
        </div>
      )}
      {msg.content}
      {msg.searches && msg.searches.length > 0 && <SearchesNote searches={msg.searches} />}
    </div>
  );
}

function SearchesNote({ searches }: { searches: ChatSearchedItem[] }) {
  return (
    <details
      style={{
        marginTop: '0.5rem',
        fontSize: '0.78rem',
        color: '#475569',
        borderTop: '1px dashed #cbd5e1',
        paddingTop: '0.4rem',
      }}
    >
      <summary style={{ cursor: 'pointer' }}>
        Searched the web {searches.length} {searches.length === 1 ? 'time' : 'times'}
      </summary>
      <div style={{ marginTop: '0.3rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
        {searches.map((s, i) => (
          <div key={i}>
            <div style={{ fontWeight: 500 }}>{s.query}</div>
            {s.hits.slice(0, 3).map((h, j) => (
              <div key={j} style={{ marginTop: '0.15rem', paddingLeft: '0.6rem' }}>
                <a href={h.link} target="_blank" rel="noopener noreferrer">
                  {h.title}
                </a>
                {h.snippet && (
                  <div style={{ color: '#64748b', fontSize: '0.75rem' }}>{h.snippet}</div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </details>
  );
}

function asMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Unknown error';
}
